import { demoEnergyDeviceList, demoEnergyMode } from "../demo/demoDevices";
import { latestGenW, toNumberOrNull } from "./energyDevices";

// Use registered device identities; none of the dummy measurements or predictions.
export const buildForecastEnergyOverview = (config, prediction = null, measurements = null, range = null, registered = undefined) => {
  const mode = demoEnergyMode(config, registered);
  const forecast = prediction?.forecast ?? [];
  const forecastOrigin = prediction?.forecastOrigin ?? null;
  const comparisonHour = prediction?.comparisonHour ?? null;
  const devices = demoEnergyDeviceList(config, registered).map((device) => {
    const cube = prediction?.deviceForecasts?.find((item) => item.deviceSn === device.serialNum);
    const tipsPole = device.deviceType === "POLE" && device.serialNum === "DEMO-P1" && prediction?.scenario === "TIPS";
    const legacyCube = device.deviceType === "CUBE" && ["DEMO-C1", "DEMO-C2", "DEMO-C3"].includes(device.serialNum)
      && prediction?.scenario === "GOVTECH" && !prediction.deviceForecasts;
    return {
      deviceSn: device.serialNum,
      deviceType: device.deviceType,
      role: device.role,
      name: device.name,
      operatingStatus: device.operatingStatus,
      isConnected: null,
      latest: null,
      history: [],
      daily: [],
      forecast: cube?.forecast ?? (tipsPole || legacyCube ? forecast : []),
      forecastHistory: (tipsPole ? prediction?.forecastHistory : cube?.forecastHistory) ?? [],
      calibration: cube?.calibration ?? null,
      forecastOrigin,
      comparisonHour,
      consumptionPending: true,
    };
  });
  const pole = devices.find((device) => device.deviceType === "POLE");
  if (pole && measurements?.deviceSn === pole.deviceSn && measurements.source === "SIMULATOR") {
    pole.latest = measurements.latest ?? null;
    pole.comparisonHour = comparisonHour ?? measurements.comparisonHour ?? null;
    pole.history = (measurements.history ?? []).map((point) => ({
      ts: point.ts, solarW: point.solarW, windW: point.windW,
      genW: point.solarW + point.windW, consW: point.consumptionW,
    }));
    pole.daily = (measurements.daily ?? []).filter((point) =>
      (!range?.from || point.statDate >= range.from) && (!range?.to || point.statDate <= range.to));
  }
  // 큐브 실측(/api/energy/govtech/measurements). BE 가 BigQuery 측정 테이블에서 직접 읽은 값이라 출처(source)는 검사하지 않는다 —
  // 시뮬레이터든 실제 큐브든 BE 가 설정으로 고른 테이블을 그대로 보여 준다(실제 전환 때 FE 수정이 없도록). 폴의 SIMULATOR 검사와 다른 점.
  const cubeMeasurements = Array.isArray(measurements?.cubes) ? measurements.cubes : [];
  devices.forEach((device) => {
    const cube = device.deviceType === "CUBE" ? cubeMeasurements.find((item) => item.deviceSn === device.deviceSn) : null;
    if (!cube) return;
    device.latest = cube.latest ?? null;
    device.comparisonHour = comparisonHour ?? measurements.comparisonHour ?? null;
    device.history = (cube.history ?? []).map((point) => ({
      ts: point.ts, solarW: point.solarW, windW: null, genW: point.solarW, consW: point.consumptionW,
    }));
  });
  // 큐브 전체 합계: 실측이 있는 큐브만 더한다(BE EnergyGroup 과 같은 규칙). 하나도 없으면 null → 화면은 "–".
  const reporting = devices.filter((device) => device.deviceType === "CUBE" && device.latest?.hasData);
  const sumLatest = (pick) => (reporting.length ? reporting.reduce((sum, device) => sum + (pick(device) ?? 0), 0) : null);
  const totalGenW = sumLatest((device) => latestGenW(device.latest, "CUBE"));
  const totalConsW = sumLatest((device) => toNumberOrNull(device.latest.currentConsumptionW));
  const historyTimes = [...new Set(devices.flatMap((device) => device.history.map((point) => new Date(point.ts).getTime())))].sort((a, b) => a - b);
  const sumPoints = (points, field) => {
    const values = points.map((point) => point?.[field]);
    // ALL sums the displayed predictions; an unknown device contributes the same 0 W fallback.
    if (field === "consW" && values.some((value) => value != null)) {
      return values.reduce((sum, value) => sum + (value ?? 0), 0);
    }
    return values.every((value) => value != null) ? values.reduce((sum, value) => sum + value, 0) : null;
  };
  const pastTimes = [...new Set(devices.flatMap((device) => device.forecastHistory.map((point) => new Date(point.ts).getTime())))].sort((a, b) => a - b);

  return {
    energy: {
      mode,
      generationForecast: prediction,
      devices,
      group: mode === "FOG" ? {
        memberCount: devices.length,
        reportingCount: reporting.length,
        totalGenW,
        totalConsW,
        surplusW: totalGenW != null && totalConsW != null ? totalGenW - totalConsW : null,
        history: historyTimes.map((time) => {
          const points = devices.map((device) => device.history.find((point) => new Date(point.ts).getTime() === time)).filter(Boolean);
          return { ts: new Date(time).toISOString(), genW: sumPoints(points, "genW"), consW: sumPoints(points, "consW") };
        }),
        daily: [],
        // Sum the per-cube predictions after calibration; actuals remain absent.
        forecast: forecast.map((point, index) => ({
          ts: point.ts,
          genW: sumPoints(devices.map((device) => device.forecast[index]), "genW"),
          consW: sumPoints(devices.map((device) => device.forecast[index]), "consW"),
        })),
        forecastHistory: pastTimes.map((time) => {
          const points = devices.map((device) => device.forecastHistory.find((point) => new Date(point.ts).getTime() === time));
          return { ts: new Date(time).toISOString(), genW: sumPoints(points, "genW"), consW: sumPoints(points, "consW") };
        }),
        forecastOrigin,
        comparisonHour,
        consumptionPending: true,
      } : null,
    },
  };
};
